import { onMounted, onUnmounted, ref } from 'vue'
import { AUTH_FAILURE_REDIRECT_EVENT, applyAuthSessionPayload, buildCasLoginUrl, peekReturnTo, saveReturnTo, type AuthSessionPayload } from '@/auth/session'
import {
  AUTH_POPUP_CLOSE_EVENT,
  AUTH_POPUP_MODE_QUERY_KEY,
  AUTH_POPUP_MODE_QUERY_VALUE,
  AUTH_POPUP_PARENT_READY_EVENT,
  AUTH_POPUP_READY_EVENT,
  AUTH_POPUP_SUCCESS_EVENT,
  AUTH_POPUP_WINDOW_NAME,
} from '@/auth/popupProtocol'

interface UseAuthPopupRecoveryOptions {
  getReturnTo?: () => string | undefined
  onAuthenticated?: () => Promise<void> | void
  listenForAuthFailureEvent?: boolean
}

function openCasLoginPopup(returnTo?: string): Window | null {
  const popupWidth = 520
  const popupHeight = 760
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - popupWidth) / 2))
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - popupHeight) / 2))
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const existingReturnTo = peekReturnTo()
  saveReturnTo(returnTo ?? existingReturnTo ?? currentPath)
  const loginUrl = new URL(buildCasLoginUrl())
  const serviceUrl = loginUrl.searchParams.get('service')
  if (serviceUrl) {
    const callbackUrl = new URL(serviceUrl)
    callbackUrl.searchParams.set(AUTH_POPUP_MODE_QUERY_KEY, AUTH_POPUP_MODE_QUERY_VALUE)
    loginUrl.searchParams.set('service', callbackUrl.toString())
  } else {
    loginUrl.searchParams.set(AUTH_POPUP_MODE_QUERY_KEY, AUTH_POPUP_MODE_QUERY_VALUE)
  }

  return window.open(
    loginUrl.toString(),
    AUTH_POPUP_WINDOW_NAME,
    `popup=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
  )
}

export function useAuthPopupRecovery(options: UseAuthPopupRecoveryOptions) {
  const loginRequired = ref(false)
  const loginInProgress = ref(false)
  const popupBlocked = ref(false)
  const loginError = ref<string | null>(null)

  let loginPopup: Window | null = null
  let popupClosedTimer: number | null = null

  function clearPopupTimer() {
    if (popupClosedTimer != null) {
      window.clearInterval(popupClosedTimer)
      popupClosedTimer = null
    }
  }

  function monitorPopupClosed() {
    clearPopupTimer()
    popupClosedTimer = window.setInterval(() => {
      if (loginPopup && !loginPopup.closed) return
      clearPopupTimer()
      loginPopup = null
      if (loginInProgress.value) {
        loginInProgress.value = false
        loginError.value = 'Login window closed before sign-in completed.'
      }
    }, 250)
  }

  function markLoginRequired() {
    loginRequired.value = true
    loginInProgress.value = false
    popupBlocked.value = false
    loginError.value = null
  }

  function clearLoginRequired() {
    loginRequired.value = false
  }

  function startLoginPopup() {
    loginError.value = null
    popupBlocked.value = false

    const popup = openCasLoginPopup(options.getReturnTo?.())
    if (!popup) {
      loginInProgress.value = false
      popupBlocked.value = true
      loginError.value = 'Popup was blocked by the browser. Allow popups and try again.'
      return
    }

    loginPopup = popup
    loginInProgress.value = true
    monitorPopupClosed()
  }

  function retryLoginPopup() {
    startLoginPopup()
  }

  async function handleAuthPayload(payload: AuthSessionPayload | undefined): Promise<boolean> {
    if (!payload || !applyAuthSessionPayload(payload)) {
      loginInProgress.value = false
      loginError.value = 'Received an invalid login response. Try again.'
      return false
    }

    loginRequired.value = false
    loginInProgress.value = false
    popupBlocked.value = false
    loginError.value = null
    clearPopupTimer()

    await options.onAuthenticated?.()
    return true
  }

  async function handleAuthPopupMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) return
    if (!event.data || typeof event.data.type !== 'string') return

    if (event.data.type === AUTH_POPUP_READY_EVENT) {
      const source = event.source as Window | null
      if (source && typeof source.postMessage === 'function') {
        source.postMessage({ type: AUTH_POPUP_PARENT_READY_EVENT }, window.location.origin)
      }
      return
    }

    if (event.data.type !== AUTH_POPUP_SUCCESS_EVENT) return
    const applied = await handleAuthPayload(event.data.payload as AuthSessionPayload | undefined)
    if (!applied) return

    const source = event.source as Window | null
    if (source && typeof source.postMessage === 'function') {
      source.postMessage({ type: AUTH_POPUP_CLOSE_EVENT }, window.location.origin)
    }
    loginPopup = null
  }

  function handleAuthFailureRedirectEvent() {
    if (!options.listenForAuthFailureEvent) return
    markLoginRequired()
  }

  onMounted(() => {
    window.addEventListener('message', handleAuthPopupMessage)
    if (options.listenForAuthFailureEvent) {
      window.addEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthFailureRedirectEvent)
    }
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleAuthPopupMessage)
    if (options.listenForAuthFailureEvent) {
      window.removeEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthFailureRedirectEvent)
    }
    clearPopupTimer()
  })

  return {
    loginRequired,
    loginInProgress,
    popupBlocked,
    loginError,
    markLoginRequired,
    clearLoginRequired,
    startLoginPopup,
    retryLoginPopup,
  }
}
